// Queries para gestión de tenants — solo accesibles desde admin (rol superadmin).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pb } from './pb';
import { formatPbError } from './pb-error';
import { presetCapabilities, defaultTimezone, type PlanPreset, type Region, type TenantCapabilities } from './capabilities';
import type { Tenant, User } from './types';

export interface TenantInput {
  name: string;
  slug: string;
  domain?: string;
  plan: PlanPreset;
  /** Región del tenant. La moneda se deriva (CL→CLP, US→USD), no es elegible. */
  region: Region;
  /** Zona horaria IANA (ej. 'America/Santiago'). Hora local de los recordatorios. */
  timezone?: string;
  /** Flags técnicos que gatean. Si se envían, GANAN sobre el preset del plan. */
  capabilities?: TenantCapabilities;
  active: boolean;
  cms_repo_url?: string;
  cms_repo_branch?: string;
  cms_github_token_env?: string;
  /**
   * PAT de GitHub guardado en DB (hidden, solo visible a _superusers).
   * En el flujo de edit del tenant: si el caller pasa string vacío, NO se
   * sobreescribe el valor existente — solo se actualiza si pasa un PAT nuevo.
   * En el wizard de create: se setea inicial.
   */
  cms_github_pat?: string;
  cms_image_optimize?: boolean;
  /** JSON con configuración del tenant (business, notifications, branding, …). */
  settings?: Record<string, unknown>;
}

export interface FirstAdminInput {
  email: string;
  name?: string;
  password: string;
  role: 'client_admin' | 'sales_rep';
}

export interface CreateTenantWithAdminResult {
  tenant: Tenant;
  admin: User | null;
  /** Password en plano — solo se devuelve para mostrarse una vez en el wizard. */
  generatedPassword: string | null;
}

export function useTenants() {
  return useQuery({
    queryKey: ['tenants', 'all'],
    queryFn: async () => {
      const list = await pb.collection('tenants').getFullList<Tenant>({
        sort: 'name',
      });
      return list;
    },
  });
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<TenantInput>) => {
      const { id, ...patch } = input;
      // El PAT (`cms_github_pat`) es un campo hidden de PB: un PATCH normal
      // desde un usuario superadmin lo DESCARTA (solo _superusers/hooks pueden
      // escribir campos hidden). Se setea aparte por el endpoint dedicado.
      const pat = (patch.cms_github_pat ?? '').trim();
      delete (patch as Partial<TenantInput>).cms_github_pat;
      const data: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined) continue;
        data[k] = v;
      }
      // capabilities explícitas (toggles del editor) GANAN. Solo si NO se
      // enviaron y cambió el plan, se derivan del preset.
      if (data.capabilities === undefined && typeof data.plan === 'string') {
        data.capabilities = presetCapabilities(data.plan as PlanPreset);
      }
      let updated: Tenant;
      try {
        updated = await pb.collection('tenants').update<Tenant>(id, data);
      } catch (err) {
        throw new Error(formatPbError(err, 'Error al guardar el tenant'));
      }
      if (pat) {
        try {
          await pb.send(`/api/tenants/${id}/cms-pat`, { method: 'POST', body: { pat } });
        } catch (err) {
          throw new Error(formatPbError(err, 'El tenant se guardó, pero el PAT de GitHub no se pudo guardar'));
        }
      }
      return updated;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });
}

/** Sube o quita el logo de marca del cliente (FormData → campo file). */
export function useUpdateTenantLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; logoFile?: File | null; clear?: boolean }) => {
      const fd = new FormData();
      if (input.clear) fd.append('logo', '');
      else if (input.logoFile instanceof File) fd.append('logo', input.logoFile);
      else throw new Error('Nada que actualizar en el logo');
      try {
        return await pb.collection('tenants').update<Tenant>(input.id, fd);
      } catch (err) {
        throw new Error(formatPbError(err, 'Error al guardar el logo'));
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });
}

/**
 * Crea un tenant y, opcionalmente, su primer usuario admin en una sola operación.
 * El user queda `verified: true` para que entre de inmediato. Si la creación del
 * user falla después de crear el tenant, el tenant igual queda — el caller puede
 * agregar el usuario después en /team. La password se devuelve en plano para
 * mostrarse una vez en el wizard.
 */
export function useCreateTenantWithAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      tenant: TenantInput;
      admin: FirstAdminInput | null;
    }): Promise<CreateTenantWithAdminResult> => {
      const t = input.tenant;
      const tenantData: Record<string, unknown> = {
        name: t.name,
        slug: t.slug,
        domain: t.domain || '',
        plan: t.plan,
        region: t.region,
        timezone: t.timezone || defaultTimezone(t.region),
        active: t.active,
        // capabilities explícitas si vienen; sino, el preset del plan.
        capabilities: t.capabilities ?? presetCapabilities(t.plan),
      };
      if (t.cms_repo_url !== undefined) tenantData.cms_repo_url = t.cms_repo_url;
      if (t.cms_repo_branch !== undefined) tenantData.cms_repo_branch = t.cms_repo_branch;
      if (t.cms_github_token_env !== undefined)
        tenantData.cms_github_token_env = t.cms_github_token_env;
      // El PAT (hidden) NO se manda en el create: PB lo descarta para usuarios
      // no-_superusers. Se setea aparte por el endpoint dedicado.
      const pat = (t.cms_github_pat ?? '').trim();
      if (t.cms_image_optimize !== undefined)
        tenantData.cms_image_optimize = t.cms_image_optimize;

      let created: Tenant;
      try {
        created = await pb.collection('tenants').create<Tenant>(tenantData);
      } catch (err) {
        throw new Error(formatPbError(err, 'Error al crear el tenant'));
      }
      if (pat) {
        try {
          await pb.send(`/api/tenants/${created.id}/cms-pat`, { method: 'POST', body: { pat } });
        } catch (err) {
          throw new Error(formatPbError(err, 'El cliente se creó, pero el PAT de GitHub no se pudo guardar'));
        }
      }

      let admin: User | null = null;
      let adminError: Error | null = null;
      if (input.admin) {
        const a = input.admin;
        const baseName = a.name?.trim() || a.email.split('@')[0];
        try {
          admin = await pb.collection('users').create<User>({
            email: a.email.trim().toLowerCase(),
            password: a.password,
            passwordConfirm: a.password,
            name: baseName,
            display_name: baseName,
            role: a.role,
            tenant: created.id,
            emailVisibility: true,
            verified: true,
          });
        } catch (err) {
          // PB devuelve mensaje top-level genérico ("Failed to create record")
          // y los detalles reales en response.data.<field>. Parseamos para que
          // el wizard muestre "Email: Value must be unique" en vez del genérico.
          const friendly = formatPbError(err, 'Error al crear el admin');
          adminError = new Error(friendly);
        }
      }

      // Releer el tenant: el hook `tenants_keygen` rellena `public_capture_key`
      // en after-create, así que el record devuelto por create() puede no tenerlo.
      const refreshed = await pb
        .collection('tenants')
        .getOne<Tenant>(created.id);

      const result: CreateTenantWithAdminResult = {
        tenant: refreshed,
        admin,
        generatedPassword: input.admin && admin ? input.admin.password : null,
      };

      if (adminError) {
        // Tenant creado, admin falló — propagar el error pero adjuntar el
        // tenant para que el caller decida si reintentar o cerrar.
        const e = new Error(
          `Tenant "${refreshed.name}" creado, pero falló crear el admin: ${adminError.message}`,
        ) as Error & { result?: CreateTenantWithAdminResult };
        e.result = result;
        throw e;
      }

      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenants'] });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// ── Resend: clave general + override por cliente ─────────────────────────
// La clave nunca vuelve en el JSON. api_key vacío en el POST no la pisa.

export interface ResendDomainCheck {
  ok: boolean;
  key_valid: boolean;
  domain?: string;
  domain_status: string;
  domain_id?: string | null;
  open_tracking: boolean | null;
  click_tracking: boolean | null;
  tracking_subdomain?: string;
  tracking_subdomain_status?: string;
  message: string;
  updated?: boolean;
}

export interface ResendSenderFields {
  from_email: string;
  from_name: string;
  reply_to: string;
  effective_from_email: string;
  effective_from_name: string;
  effective_reply_to: string;
}

export interface SystemResendStatus extends ResendSenderFields {
  configured: boolean;
  source: 'global' | 'env' | 'none' | 'tenant';
  from_domain: string;
  stored_from_domain: string;
  last4: string | null;
  env_configured: boolean;
  env_from_domain: string;
  has_stored_key: boolean;
  check?: ResendDomainCheck | null;
  warning?: string;
}

export interface TenantResendStatus extends ResendSenderFields {
  mode: 'own' | 'domain' | 'general';
  has_own_key: boolean;
  stored_from_domain: string;
  last4: string | null;
  source: string;
  from_domain: string;
  general_from_domain: string;
  general_source: string;
  general_last4?: string | null;
  general_from_email?: string;
  general_from_name?: string;
  general_reply_to?: string;
  check?: ResendDomainCheck | null;
  warning?: string;
}

export interface ResendSaveBody {
  api_key?: string;
  from_domain?: string;
  from_email?: string;
  from_name?: string;
  reply_to?: string;
  clear?: boolean;
  use_for_auth_mail?: boolean;
}

export interface AuthMailStatus {
  ok?: boolean;
  sender_name: string;
  sender_address: string;
  smtp_enabled: boolean;
  smtp_host: string;
  platform_domain: string;
}

export interface ResendWebhookInfo {
  configured: boolean;
  url: string;
  events: string[];
}

export interface ResendTestResult {
  ok: boolean;
  message_id?: string | null;
  from_email?: string;
  from_name?: string;
  message?: string;
}

async function postResend<T>(path: string, body: object) {
  try {
    return await pb.send<T>(path, { method: 'POST', body });
  } catch (err) {
    throw new Error(formatPbError(err, 'No se pudo guardar la configuración de correo'));
  }
}

export function useSystemResend(enabled = true) {
  return useQuery({
    queryKey: ['resend', 'system'],
    queryFn: () => pb.send<SystemResendStatus>('/api/system/resend', { method: 'GET' }),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useSaveSystemResend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ResendSaveBody) => postResend<SystemResendStatus>('/api/system/resend', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['resend', 'system'] }),
  });
}

export function useVerifySystemResend() {
  return useMutation({
    mutationFn: (body: { api_key?: string; from_domain?: string }) =>
      postResend<ResendDomainCheck>('/api/system/resend/verify', body),
  });
}

export function useSystemTracking() {
  return useMutation({
    mutationFn: (body: { open_tracking?: boolean; click_tracking?: boolean }) =>
      postResend<ResendDomainCheck>('/api/system/resend/tracking', body),
  });
}

export function useSystemResendTest() {
  return useMutation({
    mutationFn: (to: string) => postResend<ResendTestResult>('/api/system/resend/test', { to }),
  });
}

export function useAuthMail() {
  return useQuery({
    queryKey: ['resend', 'auth-mail'],
    queryFn: () => pb.send<AuthMailStatus>('/api/system/resend/auth-mail', { method: 'GET' }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useSaveAuthMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { sender_name?: string; sender_address?: string }) =>
      postResend<AuthMailStatus>('/api/system/resend/auth-mail', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['resend', 'auth-mail'] }),
  });
}

export function useAuthMailTest() {
  return useMutation({
    mutationFn: (to: string) => postResend<ResendTestResult>('/api/system/resend/auth-mail/test', { to }),
  });
}

export function useResendWebhook() {
  return useQuery({
    queryKey: ['resend', 'webhook'],
    queryFn: () => pb.send<ResendWebhookInfo>('/api/system/resend/webhook', { method: 'GET' }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useTenantResend(tenantId: string) {
  return useQuery({
    queryKey: ['resend', 'tenant', tenantId],
    queryFn: () => pb.send<TenantResendStatus>(`/api/tenants/${tenantId}/resend`, { method: 'GET' }),
    enabled: !!tenantId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useSaveTenantResend(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ResendSaveBody) =>
      postResend<TenantResendStatus>(`/api/tenants/${tenantId}/resend`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['resend', 'tenant', tenantId] }),
  });
}

export function useVerifyTenantResend(tenantId: string) {
  return useMutation({
    mutationFn: (body: { api_key?: string; from_domain?: string }) =>
      postResend<ResendDomainCheck>(`/api/tenants/${tenantId}/resend/verify`, body),
  });
}

export function useTenantTracking(tenantId: string) {
  return useMutation({
    mutationFn: (body: { open_tracking?: boolean; click_tracking?: boolean }) =>
      postResend<ResendDomainCheck>(`/api/tenants/${tenantId}/resend/tracking`, body),
  });
}

export function useTenantResendTest(tenantId: string) {
  return useMutation({
    mutationFn: (to: string) =>
      postResend<ResendTestResult>(`/api/tenants/${tenantId}/resend/test`, { to }),
  });
}
